import { hexToRgba } from '@/components/ColorPicker/utils/hexToRgba';
import SmallClose from '@/images/SmallClose.svg';
import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { HexColorInput, RgbaColor, RgbaColorPicker } from 'react-colorful';
import { rgbaToHex } from './utils/rgbaToHex';

const Container = styled.div`
    display: flex;
    width: 289px;
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    flex-shrink: 0;
    border-radius: 8px;
    border: 1px solid ${({ theme }) => theme.colors.white};
    background: ${({ theme }) => theme.colors.white};
    box-shadow:
        0px 0px 6px 0px rgba(31, 41, 55, 0.05),
        0px 10px 15px 0px rgba(31, 41, 55, 0.1);

    .react-colorful {
        gap: 16px;
        background: none !important;
    }

    .react-colorful__saturation {
        flex: 1 0 0;
        align-self: stretch;
        border-radius: 8px;
        border-bottom-width: 0px;
    }

    .react-colorful__hue {
        height: 14px;
        flex-shrink: 0;
        align-self: stretch;
        border-radius: 100px;
    }

    .react-colorful__alpha {
        display: none;
        width: 256.833px;
        height: 14px;
        flex-shrink: 0;
        border-radius: 116.667px;
    }
`;

const StyledRgbaColorPicker = styled(RgbaColorPicker)`
    width: 100% !important;
    min-height: 200px;
    height: 100% !important;
    border-radius: 8px !important;
    background: linear-gradient(90deg, rgba(79, 70, 229, 0) 0%, #4f46e5 100%), linear-gradient(0deg, #000 0%, rgba(196, 196, 196, 0) 100%) !important;
`;

const CustomPicker = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;
    margin-top: 8px;
    margin-bottom: 8px;
`;

const ColorTextDiv = styled.div`
    display: flex;
    width: 212px;
    padding: 4px 6px;
    align-items: center;
    gap: 4px;
    border-radius: 4px;
    height: 28px;
    border: 1px solid ${({ theme }) => theme.colors.settings};
`;

const CurrentColor = styled.div<{ color: string }>`
    width: 16px;
    height: 16px;
    border-radius: 1.333px;
    border: 0.667px solid #d8d8d7;
    background: ${({ color }) => color};
`;

const StyledHexColorInput = styled(HexColorInput)`
    color: ${({ theme }) => theme.colors.grey[700]};
    text-align: center;
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    border: none;
    text-align: left;
`;

const HexString = styled(Typography)`
    color: ${({ theme }) => theme.colors.grey[400]};
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
`;

const StyledTypography = styled(Typography)`
    display: flex;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    color: ${({ theme }) => theme.colors.grey[700]};
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.24px;
`;

const CloseIcon = styled.img`
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    cursor: pointer;
`;

const Title = styled.div`
    display: flex;
    width: 257px;
    justify-content: space-between;
    align-items: center;
`;

const ColorsContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: space-between;
`;

const Color = styled.div<{ color: string }>`
    width: 24px;
    min-height: 24px;
    border-radius: 2px;
    background: ${({ color }) => color};
    cursor: pointer;
    border: 1px solid ${({ theme }) => theme.colors.black[200]};
`;

interface Props {
    color: string;
    setColor: (color: string) => void;
    setClose: () => void;
    predefinedColors?: string[];
}

export const ColorPickerPopover = ({ color, setColor, setClose, predefinedColors }: Props) => {
    const handleHexChange = (color: string) => {
        setColor(color);
    };

    const handleRgbaChange = (color: RgbaColor) => {
        setColor(rgbaToHex(color));
    };

    return (
        <Container>
            <Title>
                <StyledTypography>Colors</StyledTypography>
                <CloseIcon src={SmallClose} onClick={setClose} />
            </Title>
            <StyledRgbaColorPicker color={hexToRgba(color)} onChange={handleRgbaChange} />
            <CustomPicker>
                <ColorTextDiv>
                    <CurrentColor color={color} />
                    <HexString>#</HexString>
                    <StyledHexColorInput color={color} onChange={handleHexChange} />
                </ColorTextDiv>
            </CustomPicker>
            <ColorsContainer>{predefinedColors?.map(c => <Color key={c} color={c} onClick={() => setColor(c)} />)}</ColorsContainer>
        </Container>
    );
};
